import type { SharedRebateProgramsParams } from "./SharedRebateProgramsContext";

import { type Hoid, programDisplayStatus } from "../data";

function columnCompareValue(key: string, h: Hoid): string | number {
  switch (key) {
    case "program":
      return h.rebateProgramLabel.toLowerCase();
    case "subibs":
      return (h.partners[0]?.name ?? "").toLowerCase();
    case "split":
      return h.masterSplitPct;
    case "lastPayment":
      return h.lastPayment ? new Date(h.lastPayment).getTime() : 0;
    case "clients":
      return h.clientAccounts;
    case "totalRebate":
      return h.totalRebate;
    case "yourPayout":
      return h.myPayout;
    default:
      return 0;
  }
}

export type ProgramsPageResult = {
  rows: Hoid[];
  totalCount: number;
};

/** Pure filter → sort → paginate over the full (unfiltered) program list. */
export function computeProgramsPage(
  allPrograms: Hoid[],
  params: SharedRebateProgramsParams,
): ProgramsPageResult {
  let rows = allPrograms;

  const q = params.search.trim().toLowerCase();
  if (q) {
    rows = rows.filter(
      (h) =>
        h.rebateProgramLabel.toLowerCase().includes(q) ||
        h.id.includes(q) ||
        h.partners.some((p) => p.name.toLowerCase().includes(q)),
    );
  }

  if (params.selected.subIb?.length) {
    const set = new Set(params.selected.subIb);
    rows = rows.filter((h) => h.partners.some((p) => set.has(p.subIbId)));
  }
  if (params.selected.status?.length) {
    const set = new Set(params.selected.status);
    rows = rows.filter((h) => set.has(programDisplayStatus(h)));
  }

  // Default order (no explicit sortBy) is a backend concern — production
  // omits sortColumn and takes whatever order the API returns, and the mock
  // doesn't try to emulate it (PART-4130, follow-up ticket owns the BE side).
  // Same for date-range earnings scoping: the real `earn` CTE already filters
  // total_earning/your_payout by the payment date range server-side, so the
  // mock doesn't try to replicate that business logic either.
  const sorted = params.sortBy
    ? [...rows].sort((a, b) => {
        const sign = params.sortOrder === "asc" ? 1 : -1;
        const av = columnCompareValue(params.sortBy as string, a);
        const bv = columnCompareValue(params.sortBy as string, b);
        if (av < bv) return -1 * sign;
        if (av > bv) return 1 * sign;
        return 0;
      })
    : rows;

  const totalCount = sorted.length;
  const start = params.page * params.pageSize;

  return {
    rows: sorted.slice(start, start + params.pageSize),
    totalCount,
  };
}
